import { actorRelations } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { getTableName } from "@-/com.npmjs.drizzle-orm";
import type { SQLiteTable } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import type { ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import type { RelationalReadConfig } from "@-/dev.improve.haniverse.lib.database";
import { useMemo } from "react";
import {
  getWithClause,
  type RelationsMetadata,
  resolveTableChangeTables,
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";

type ActorQueryTable = keyof ActorDatabaseHandle["query"] & string;

// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- reinterpret the runtime-verified `{ table, relations }` shape the resolver needs
const actorRelationsMetadata = actorRelations as unknown as RelationsMetadata<SQLiteTable>;

/**
 * Registers query keys for changes to the actor tables a relational read
 * depends on: its main table plus every table reachable through `config.with`.
 */
export function useActorTableChangeVersion<TTable extends ActorQueryTable>(
  mainTable: TTable,
  config?: RelationalReadConfig<ActorDatabaseHandle, TTable>,
) {
  const withClause = getWithClause(config);
  const withKey = JSON.stringify(withClause ?? null);
  const tables = useMemo(
    () => resolveTableChangeTables(actorRelationsMetadata, getTableName, mainTable, withClause),
    // `withClause` is captured via its stable serialization `withKey`.
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- withKey stands in for withClause
    [mainTable, withKey],
  );

  return useTableChangeVersion(tableNameMatcher(tables));
}
